import { useCallback, useEffect, useRef, useState } from "react";
import { freshMarketChange, freshMarketPrice } from "../utils/market-prices";

/** Public quotes only; multiplication by private balances stays in the browser. */
export function useMarketPrices(assets: Readonly<Record<string, string>>) {
  const [quotes, setQuotes] = useState<{
    prices: Record<string, number>;
    changes: Record<string, number>;
  }>({ prices: {}, changes: {} });
  const active = useRef(false);
  const request = useRef<AbortController | null>(null);
  const lastFetch = useRef<number | null>(null);
  const refresh = useCallback(async () => {
    if (
      !active.current ||
      request.current ||
      (lastFetch.current !== null && Date.now() - lastFetch.current < 30_000)
    )
      return;
    lastFetch.current = Date.now();
    const controller = new AbortController();
    request.current = controller;
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetch(
        "/api/proxy/coingecko?path=/api/v3/simple/price",
        { signal: controller.signal },
      );
      if (!response.ok) throw new Error("Prices unavailable");
      const data = await response.json();
      const prices: Record<string, number> = {};
      const changes: Record<string, number> = {};
      for (const [asset, id] of Object.entries(assets)) {
        const price = freshMarketPrice(data?.[id]);
        if (price === null) continue;
        prices[asset] = price;
        const change = freshMarketChange(data?.[id]);
        if (change !== null) changes[asset] = change;
      }
      if (
        active.current &&
        request.current === controller &&
        !controller.signal.aborted
      )
        setQuotes({ prices, changes });
    } catch {
      if (active.current && request.current === controller)
        setQuotes({ prices: {}, changes: {} });
    } finally {
      clearTimeout(timeout);
      if (request.current === controller) request.current = null;
    }
  }, [assets]);
  useEffect(() => {
    active.current = true;
    lastFetch.current = null;
    setQuotes({ prices: {}, changes: {} });
    void refresh();
    const timer = setInterval(() => void refresh(), 60_000);
    return () => {
      active.current = false;
      request.current?.abort();
      request.current = null;
      clearInterval(timer);
    };
  }, [refresh]);
  return { prices: quotes.prices, changes: quotes.changes, refresh };
}
